
import { useEffect, useRef, useState } from 'react';

interface SmartAdUnitProps {
  format: 'banner' | 'display' | 'native' | 'video';
  size?: { width: number; height: number };
  className?: string;
}

declare global {
  interface Window {
    adsbygoogle: any[];
    AdProvider?: any[];
  }
}

export default function SmartAdUnit({ format, size, className = '' }: SmartAdUnitProps) {
  const adRef = useRef<HTMLDivElement>(null);
  const [network, setNetwork] = useState<'adsense' | 'exoclick'>('adsense');
  const isInitialized = useRef(false);
  const [isReady, setIsReady] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    // 50/50 split between AdSense and ExoClick
    const networks: Array<'adsense' | 'exoclick'> = ['adsense', 'exoclick'];
    const selected = networks[Math.floor(Math.random() * networks.length)];
    setNetwork(selected);
  }, []);

  useEffect(() => {
    let checkCount = 0;
    const maxChecks = 100;
    
    const checkContainer = () => {
      if (adRef.current && checkCount < maxChecks) {
        const rect = adRef.current.getBoundingClientRect();
        const computedStyle = window.getComputedStyle(adRef.current);
        const hasWidth = rect.width > 0 || parseInt(computedStyle.width) > 0;
        const hasHeight = rect.height > 0 || parseInt(computedStyle.height) > 0;
        
        if (hasWidth && hasHeight && rect.width >= 250) {
          setIsReady(true);
        } else {
          checkCount++;
          setTimeout(checkContainer, 100);
        }
      }
    };

    setTimeout(checkContainer, 1000);
  }, []);

  useEffect(() => {
    if (!isReady || isInitialized.current) return;

    const loadAd = () => {
      if (network === 'adsense') {
        if (adRef.current) {
          const rect = adRef.current.getBoundingClientRect();
          const computedStyle = window.getComputedStyle(adRef.current);
          const actualWidth = rect.width || parseInt(computedStyle.width) || 0;
          
          if (actualWidth < 250) {
            console.warn('AdSense container width too small:', actualWidth);
            return;
          }

          const insElement = adRef.current.querySelector('.adsbygoogle');
          if (insElement && insElement.getAttribute('data-adsbygoogle-status')) {
            return;
          }
        }

        if (!document.querySelector('script[src*="adsbygoogle.js"]')) {
          const script = document.createElement('script');
          script.src = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-1204278699094462';
          script.async = true;
          script.crossOrigin = 'anonymous';
          document.head.appendChild(script);
          
          script.onload = () => {
            setTimeout(() => {
              try {
                if (adRef.current) {
                  const rect = adRef.current.getBoundingClientRect();
                  const insElement = adRef.current.querySelector('.adsbygoogle');
                  
                  if (insElement && !insElement.getAttribute('data-adsbygoogle-status') && rect.width >= 250) {
                    (window.adsbygoogle = window.adsbygoogle || []).push({});
                  }
                }
              } catch (err) {
                console.error('AdSense error:', err);
              }
            }, 2000);
          };
        } else {
          setTimeout(() => {
            try {
              if (adRef.current) {
                const rect = adRef.current.getBoundingClientRect();
                const insElement = adRef.current.querySelector('.adsbygoogle');
                
                if (insElement && !insElement.getAttribute('data-adsbygoogle-status') && rect.width >= 250) {
                  (window.adsbygoogle = window.adsbygoogle || []).push({});
                }
              }
            } catch (err) {
              console.error('AdSense error:', err);
            }
          }, 2000);
        }
      } else if (network === 'exoclick') {
        if (!document.querySelector('script[src*="a.magsrv.com"]')) {
          const script = document.createElement('script');
          script.src = 'https://a.magsrv.com/ad-provider.js';
          script.async = true;
          script.type = 'application/javascript';
          document.head.appendChild(script);
          
          script.onload = () => {
            setTimeout(() => {
              try {
                (window.AdProvider = window.AdProvider || []).push({"serve": {}});
              } catch (err) {
                console.error('ExoClick error:', err);
              }
            }, 300);
          };
        } else {
          setTimeout(() => {
            try {
              (window.AdProvider = window.AdProvider || []).push({"serve": {}});
            } catch (err) {
              console.error('ExoClick error:', err);
            }
          }, 300);
        }
      }
    };

    setTimeout(loadAd, 1500);
    isInitialized.current = true;
  }, [network, isReady]);

  const getAdSlot = () => {
    if (format === 'banner') return '1076334013';
    if (format === 'display') return '2089667346';
    if (format === 'native') return '3102900679';
    if (format === 'video') return '4116233012';
    return '1076334013';
  };

  const getResponsiveDimensions = () => {
    if (size) return size;
    
    // Mobile-friendly sizes
    if (isMobile) {
      if (format === 'banner') return { width: 320, height: 50 };
      if (format === 'display') return { width: 300, height: 250 };
      if (format === 'native') return { width: 300, height: 250 };
      if (format === 'video') return { width: 320, height: 180 };
    }
    
    // Desktop sizes
    if (format === 'banner') return { width: 728, height: 90 };
    if (format === 'display') return { width: 300, height: 250 };
    if (format === 'native') return { width: 300, height: 250 };
    if (format === 'video') return { width: 640, height: 360 };
    return { width: 300, height: 250 };
  };

  const dimensions = getResponsiveDimensions();

  return (
    <div 
      ref={adRef} 
      className={`${className} ad-container bg-black/30 border-2 border-gray-800 rounded-lg overflow-hidden flex flex-col items-center justify-center p-2 mx-auto`}
      style={{ 
        width: `${dimensions.width}px`,
        minWidth: `${dimensions.width}px`,
        maxWidth: `${dimensions.width}px`,
        minHeight: `${dimensions.height}px`,
        height: `${dimensions.height}px`
      }}
    >
      <div className="text-center text-xs text-gray-500 mb-2 font-semibold">Advertisement</div>
      
      {isReady && network === 'adsense' && (
        <ins
          className="adsbygoogle"
          style={{ 
            display: 'block', 
            width: `${dimensions.width - 16}px`,
            height: `${dimensions.height - 40}px`,
            minWidth: `${dimensions.width - 16}px`,
            minHeight: `${dimensions.height - 40}px`
          }}
          data-ad-client="ca-pub-1204278699094462"
          data-ad-slot={getAdSlot()}
          data-ad-format="auto"
          data-full-width-responsive="true"
        ></ins>
      )}

      {isReady && network === 'exoclick' && (
        <ins className="eas6a97888e2" data-zoneid="5776846"></ins>
      )}
    </div>
  );
}
